import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import EmailCapture from './EmailCapture';
import { trackEvent } from './analytics';
import './App.css';

const MANIFEST_URL = '/blog-content/_index.json';
const CONTENT_BASE = '/blog-content/posts/';

function formatDate(iso) {
  if (!iso) return '';
  const d = iso instanceof Date ? iso : new Date(typeof iso === 'string' ? iso + 'T00:00:00Z' : iso);
  if (Number.isNaN(d.getTime())) return typeof iso === 'string' ? iso : '';
  // A calendar date read as UTC midnight: format it in UTC too, or every reader west of
  // Greenwich sees the day before.
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
}

// Coerce a Firestore Timestamp (or ISO string, or Date) into a sortable ISO date string.
function toIsoDate(v) {
  if (!v) return '';
  if (typeof v === 'string') return v;
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v.toDate === 'function') return v.toDate().toISOString().slice(0, 10);
  if (v.seconds) return new Date(v.seconds * 1000).toISOString().slice(0, 10);
  return '';
}

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);   // metadata (from static manifest OR Firestore)
  const [body, setBody] = useState(null);   // markdown body
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    let cancelled = false;
    (async () => {
      try {
        // Strategy 1: static manifest first (git-tracked legacy posts; CDN-cached, fast)
        const mRes = await fetch(MANIFEST_URL, { cache: 'no-cache' });
        if (mRes.ok) {
          const m = await mRes.json();
          const staticPost = (m.posts || []).find((p) => p.slug === slug);
          if (staticPost && !cancelled) {
            const fileURL = `${CONTENT_BASE}${staticPost.file || (staticPost.slug + '.md')}`;
            const r = await fetch(fileURL, { cache: 'no-cache' });
            if (r.ok) {
              const text = await r.text();
              if (!cancelled) {
                setPost(staticPost);
                setBody(text);
                return;
              }
            }
          }
        }
        // Strategy 2: fall through to Firestore blog_posts/{slug} (Phase Blog — admin-published)
        const db = getFirestore();
        const snap = await getDoc(doc(db, 'blog_posts', slug));
        if (snap.exists() && !cancelled) {
          const data = snap.data();
          setPost({
            slug: data.slug || slug,
            title: data.title,
            summary: data.summary,
            author: data.author,
            tags: data.tags,
            heroImage: data.heroImage,
            series: data.series,
            publishedAt: toIsoDate(data.publishedAt),
            readingMinutes: data.readingMinutes,
          });
          setBody(data.markdown || '');
          return;
        }
        throw new Error('Post not found.');
      } catch (err) {
        if (!cancelled) setError(err.message || String(err));
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Set the document title for SEO / share previews (a no-op outside the browser).
  useEffect(() => {
    if (post?.title) {
      const previous = document.title;
      document.title = `${post.title} — mChatAI`;
      return () => { document.title = previous; };
    }
  }, [post]);

  // Count a blog read once the post metadata resolves (fires after the SPA
  // page_view; tells us which posts actually get opened, not just the /blog list).
  useEffect(() => {
    if (post?.slug) trackEvent('blog_post_read', { slug: post.slug });
  }, [post?.slug]);

  const markdownComponents = useMemo(() => ({
    h1: ({ children }) => (
      <h1 style={{ fontSize: '2.4rem', fontWeight: 800, color: '#111827', margin: '0 0 24px', lineHeight: 1.15 }}>{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', margin: '40px 0 12px' }}>{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#111827', margin: '28px 0 8px' }}>{children}</h3>
    ),
    p: ({ children }) => (
      <p style={{ color: '#374151', lineHeight: 1.75, marginBottom: 16, fontSize: '1.04rem' }}>{children}</p>
    ),
    ul: ({ children }) => (
      <ul style={{ color: '#374151', lineHeight: 1.8, marginBottom: 16, paddingLeft: 24, fontSize: '1.04rem' }}>{children}</ul>
    ),
    ol: ({ children }) => (
      <ol style={{ color: '#374151', lineHeight: 1.8, marginBottom: 16, paddingLeft: 24, fontSize: '1.04rem' }}>{children}</ol>
    ),
    a: ({ href, children, ...rest }) => (
      <a href={href} style={{ color: '#3b82f6', textDecoration: 'underline' }} {...rest}>{children}</a>
    ),
    img: ({ src, alt }) => (
      <img
        src={src}
        alt={alt || ''}
        loading="lazy"
        style={{
          display: 'block',
          maxWidth: '100%',
          maxHeight: 560,
          width: 'auto',
          height: 'auto',
          margin: '24px auto',
          borderRadius: 10,
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}
      />
    ),
    code: ({ inline, children, ...rest }) => (
      inline
        ? <code style={{ background: '#f3f4f6', padding: '1px 6px', borderRadius: 4, fontSize: '0.92em', color: '#111827' }} {...rest}>{children}</code>
        : <code {...rest}>{children}</code>
    ),
    pre: ({ children }) => (
      <pre style={{
        background: '#0f172a', color: '#e2e8f0', padding: 16, borderRadius: 8,
        overflowX: 'auto', fontSize: '0.88rem', lineHeight: 1.5, marginBottom: 20
      }}>{children}</pre>
    ),
    blockquote: ({ children }) => (
      <blockquote style={{
        borderLeft: '3px solid #d1d5db', paddingLeft: 16, color: '#4b5563',
        margin: '16px 0', fontStyle: 'italic'
      }}>{children}</blockquote>
    ),
  }), []);

  return (
    <div className="mp-page" style={{ background: '#fff', minHeight: 'calc(100vh - 60px)' }}>
      <article style={{ maxWidth: 720, margin: '0 auto', padding: '64px 24px 32px' }}>
        <div style={{ marginBottom: 12, fontSize: '0.9rem' }}>
          <Link to="/blog" style={{ color: '#3b82f6', textDecoration: 'none' }}>← Back to all posts</Link>
        </div>

        {error && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b',
            padding: 16, borderRadius: 8, marginBottom: 24
          }}>
            {error}
          </div>
        )}

        {post && (
          <header style={{ marginBottom: 32, color: '#6b7280', fontSize: '0.9rem' }}>
            {post.series && (
              <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '2px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600, marginRight: 8 }}>
                {post.series}
              </span>
            )}
            <span>{formatDate(post.publishedAt)}</span>
            {post.author && <span> · by {post.author}</span>}
            {post.readingMinutes && <span> · {post.readingMinutes} min read</span>}
          </header>
        )}

        {!body && !error && (
          <p style={{ color: '#6b7280' }}>Loading…</p>
        )}

        {body && (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {body}
          </ReactMarkdown>
        )}
      </article>

      <section style={{ maxWidth: 720, margin: '0 auto', padding: '48px 24px 96px' }}>
        <EmailCapture
          source={`blog-post:${slug}`}
          title="Liked this?"
          subtitle="Get one short post like this in your inbox each Sunday."
        />
      </section>
    </div>
  );
};

export default BlogPostPage;
